import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AutoComplete,
  Button,
  Checkbox,
  Input,
  Radio,
  Select,
  Spin,
  Tooltip,
  message,
} from "antd";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Check,
  Gauge,
  Network,
  PlayCircle,
  Shield,
  ShieldCheck,
} from "lucide-react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { AN_COLORS, AN_FONT, AN_MONO, anAlpha } from "../../../design-system/tokens";
import { DOMAIN_ID, ORG_ID } from "../../../constants";
import { dispatchUpdateDomainEnforcement } from "../../../utils/apiservice";
import {
  createADPolicy,
  getAllADDomains,
  getEffectivePolicy,
  listADGroups,
  listADPolicies,
  listADUsers,
  listOUs,
  previewPolicyImpact,
  updateADPolicy,
} from "../../../service/adPolicyService";
import { ActionPill } from "./adPolicyTokens";
import {
  ACTIONS,
  DEFAULT_MFA_PROMPT,
  DESTINATION_HINT,
  MFA_VARIABLES,
  PROTOCOLS,
  RETIRED_ACTION_NOTICE,
  SOURCE_HINT,
  cleanTokens,
  domainFallback,
  domainFqdn,
  domainLabel,
  domainPosture,
  invalidTokens,
  isRetiredAction,
  isValidDestination,
  isValidSource,
  normalizeAction,
  renderMfaPrompt,
  sanitizeProtocols,
  unknownVariables,
} from "./adPolicyModel";

const AUTH_TYPES = [
  { value: "ad",     label: "Active Directory", sub: "Kerberos · NTLM · LDAP · SMB", icon: Shield },
  // Entra ID not yet available — re-enable when entra-service ships.
  // { value: "entra_id", label: "Entra ID", sub: "Cloud-joined", icon: Globe },
  // RADIUS has no evaluator behind it yet; authTypes is always ["ad"].
  { value: "radius", label: "RADIUS", sub: "VPN / Wi-Fi", icon: Network, disabled: true },
];

const POLICY_KINDS = [
  {
    value: "static",
    label: "Static",
    sub: "Same action every time",
    icon: ShieldCheck,
  },
  // Risk scoring isn't wired into the evaluator — no mfaConfig.stepUpOnRisk or
  // riskThreshold is sent while this is off.
  {
    value: "risk_based",
    label: "Risk-Based",
    sub: "Reacts to identity risk",
    icon: Gauge,
    disabled: true,
  },
];

// listADGroups / listADUsers page size. The server defaults to 50 and caps at
// 500; anything past the first page is reached by typing into the filter.
const PICKER_PAGE_SIZE = 50;
const PICKER_DEBOUNCE_MS = 250;
const EMPTY_PICKER = { loading: false, options: [], total: 0, error: "" };

const ACTION_TONES = {
  green:      { fg: "var(--an-success)", bg: "var(--an-successBg)", bd: "var(--an-successBd)" },
  red:        { fg: "var(--an-danger)", bg: "var(--an-dangerBg)", bd: "var(--an-dangerBd)" },
  "red-dark": { fg: "var(--an-dangerFg)", bg: "var(--an-dangerBg)", bd: "var(--an-dangerBd)" },
};

export default function CreatePolicyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { policyId: routePolicyId } = useParams();
  const isEdit = !!routePolicyId;
  const [messageApi, messageContext] = message.useMessage();

  // ─── Data fetched on mount ────────────────────────────────────────────────
  const [domains, setDomains] = useState([]);
  const [editingPolicy, setEditingPolicy] = useState(
    location.state?.policy || null,
  );
  const [bootstrapping, setBootstrapping] = useState(isEdit && !editingPolicy);

  // ─── Wizard ───────────────────────────────────────────────────────────────
  const [step, setStep] = useState(1);

  // ─── Form state ───────────────────────────────────────────────────────────
  const [policyName, setPolicyName] = useState("");
  const [staticAction, setStaticAction] = useState("mfa_required");
  const [adDomainId, setAdDomainId] = useState(null);
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [ous, setOus] = useState([]);
  const [matchAll, setMatchAll] = useState(false);
  const [protocols, setProtocols] = useState([]);
  const [sourceMode, setSourceMode] = useState("all");
  const [sources, setSources] = useState([]);
  const [destMode, setDestMode] = useState("all");
  const [destinations, setDestinations] = useState([]);
  const [mfaPrompt, setMfaPrompt] = useState(DEFAULT_MFA_PROMPT);

  // Set when we load a policy that still carries a retired or unsupported
  // setting, so the form can say what it silently changed.
  const [legacyNotes, setLegacyNotes] = useState([]);

  // ─── Lookups ──────────────────────────────────────────────────────────────
  // Groups and users are searched server-side: `filter` is a case-insensitive
  // substring, and the response is one page out of `total`.
  const [groupQuery, setGroupQuery] = useState("");
  const [groupPicker, setGroupPicker] = useState(EMPTY_PICKER);
  const [userQuery, setUserQuery] = useState("");
  const [userPicker, setUserPicker] = useState(EMPTY_PICKER);
  const [ouOptions, setOuOptions] = useState([]);
  const [ouLoading, setOuLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // ─── Review & publish ─────────────────────────────────────────────────────
  const [impact, setImpact] = useState({ state: "idle", matchedUsers: null });
  const [conflicts, setConflicts] = useState(null); // { conflicts: [], warning }
  const [postureSaving, setPostureSaving] = useState(false);
  const [dryRun, setDryRun] = useState({ state: "idle", result: null });
  const [dryRunUser, setDryRunUser] = useState("");
  const [dryRunProtocol, setDryRunProtocol] = useState("kerberos");

  const selectedDomain = useMemo(
    () => (domains || []).find((d) => d.id === adDomainId) || null,
    [domains, adDomainId],
  );
  const adDomainFqdn = domainFqdn(selectedDomain);
  const adDomainLabel = domainLabel(selectedDomain);
  const posture = domainPosture(selectedDomain);

  // Load AD domains on mount
  useEffect(() => {
    getAllADDomains({
      payload: { orgId: ORG_ID(), tenantId: DOMAIN_ID() },
      onSuccess: (res) => setDomains(res?.domains || res?.data || []),
      onFailure: () => {},
    });
  }, []);

  // If edit mode and no policy in router state, fetch the full list and find it.
  useEffect(() => {
    if (!isEdit || editingPolicy) return;
    setBootstrapping(true);
    listADPolicies({
      payload: {
        orgId: ORG_ID(),
        tenantId: DOMAIN_ID(),
        policyType: "AD",
        page: 1,
        limit: 500,
      },
      onSuccess: (res) => {
        const list = res?.policies || res?.data || [];
        const found = list.find((p) => (p.id || p.policyId) === routePolicyId);
        if (found) {
          setEditingPolicy(found);
        } else {
          messageApi.error("Policy not found");
          navigate("/pam/policy/v2");
        }
        setBootstrapping(false);
      },
      onFailure: () => {
        messageApi.error("Failed to load policy");
        setBootstrapping(false);
      },
    });
  }, [isEdit, editingPolicy, routePolicyId, messageApi, navigate]);

  // Hydrate form fields from editingPolicy once we have it AND we have domains.
  useEffect(() => {
    if (editingPolicy && domains.length > 0) {
      const j = editingPolicy.policyJson || {};
      const ad = j.ad || {};
      const notes = [];
      setPolicyName(editingPolicy.policyName || "");

      // Retired and unsupported settings are normalised on load rather than
      // round-tripped, so what the form shows is what will be published.
      if (isRetiredAction(j.policyFlow)) notes.push(RETIRED_ACTION_NOTICE);
      if (j.policyKind === "risk_based") {
        notes.push(
          "This policy was created with risk-based evaluation, which the evaluator doesn't run. It is shown — and will be saved — as a static policy using the above-threshold action.",
        );
      }
      if (j.policyMode && j.policyMode !== "online") {
        notes.push(
          `Per-policy mode ("${j.policyMode}") has been removed — enforcement is a property of the domain now.`,
        );
      }
      setStaticAction(
        normalizeAction(
          j.policyKind === "risk_based"
            ? j.aboveThresholdFlow || j.policyFlow
            : j.policyFlow,
        ),
      );

      // Prefer the id the policy stored; fall back to matching on FQDN for
      // policies written before ad.adId existed.
      const byId = domains.find((d) => d.id === ad.adId);
      const byName = domains.find(
        (d) => domainFqdn(d) === ad.domain || domainLabel(d) === ad.domain,
      );
      setAdDomainId(byId?.id ?? byName?.id ?? domains[0]?.id ?? null);

      setGroups(ad.groups || []);
      setUsers(ad.users || []);
      setOus(ad.ous || []);
      setMatchAll(!!ad.matchAll);
      const noTargets =
        (ad.groups || []).length === 0 &&
        (ad.users || []).length === 0 &&
        (ad.ous || []).length === 0;
      if (noTargets && !ad.matchAll) {
        notes.push(
          "This policy has no targets and no domain-wide opt-in, so it matches nobody. Pick a scope, or tick “Apply to every user in this domain”, before saving.",
        );
      }
      setProtocols(sanitizeProtocols(j.protocolControl?.allowed));
      const src = cleanTokens(ad.sources);
      setSources(src);
      setSourceMode(src.length > 0 ? "specific" : "all");
      const dst = cleanTokens(ad.destinations);
      setDestinations(dst);
      setDestMode(dst.length > 0 ? "specific" : "all");
      setMfaPrompt(j.mfaPromptTemplate || DEFAULT_MFA_PROMPT);
      setLegacyNotes(notes);
    } else if (!editingPolicy && domains.length > 0 && adDomainId == null) {
      setAdDomainId(domains[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingPolicy, domains]);

  // AD groups for the picked domain. Re-runs on every keystroke in the picker,
  // debounced, because the filter is applied server-side.
  useEffect(() => {
    if (!adDomainId) {
      setGroupPicker(EMPTY_PICKER);
      return undefined;
    }
    const timer = setTimeout(() => {
      setGroupPicker((prev) => ({ ...prev, loading: true, error: "" }));
      listADGroups({
        payload: {
          org_Id: ORG_ID(),
          tenant_Id: DOMAIN_ID(),
          adId: adDomainId,
          filter: groupQuery,
          page: 1,
          pageSize: PICKER_PAGE_SIZE,
        },
        onSuccess: (res) => {
          const list = Array.isArray(res?.data) ? res.data : [];
          setGroupPicker({
            loading: false,
            error: "",
            total: res?.total ?? list.length,
            // Only groupName goes into the policy; id is a display key.
            options: list.map((g) => ({
              value: g.groupName,
              label:
                g.memberCount != null
                  ? `${g.groupName} · ${g.memberCount} ${
                      g.memberCount === 1 ? "user" : "users"
                    }`
                  : g.groupName,
            })),
          });
        },
        onFailure: (e) =>
          setGroupPicker({
            ...EMPTY_PICKER,
            error: e?.message || "Couldn't load AD groups.",
          }),
      });
    }, PICKER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [adDomainId, groupQuery]);

  // AD users for the picked domain — its own endpoint and its own filter, not
  // derived from the selected groups.
  useEffect(() => {
    if (!adDomainId) {
      setUserPicker(EMPTY_PICKER);
      return undefined;
    }
    const timer = setTimeout(() => {
      setUserPicker((prev) => ({ ...prev, loading: true, error: "" }));
      listADUsers({
        payload: {
          org_Id: ORG_ID(),
          tenant_Id: DOMAIN_ID(),
          adId: adDomainId,
          filter: userQuery,
          page: 1,
          pageSize: PICKER_PAGE_SIZE,
        },
        onSuccess: (res) => {
          const list = Array.isArray(res?.data) ? res.data : [];
          setUserPicker({
            loading: false,
            error: "",
            total: res?.total ?? list.length,
            options: list.map((u) => ({
              value: u.username,
              label: u.username,
              sub: u.logoname || u.email || "",
            })),
          });
        },
        onFailure: (e) =>
          setUserPicker({
            ...EMPTY_PICKER,
            error: e?.message || "Couldn't load AD users.",
          }),
      });
    }, PICKER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [adDomainId, userQuery]);

  // OUs — refetch on domain change
  useEffect(() => {
    if (!adDomainId) {
      setOuOptions([]);
      return;
    }
    setOuLoading(true);
    setOus((prev) => (editingPolicy ? prev : []));
    listOUs({
      payload: {
        org_Id: ORG_ID(),
        tenant_Id: DOMAIN_ID(),
        adId: adDomainId,
        domain: adDomainFqdn,
      },
      onSuccess: (res) => {
        const list = res?.data || res?.ous || [];
        // listOU answers with every OU the tenant knows about, keyed by
        // `ad_id` — it does not scope to the adId in the request. Rows for
        // other directories have to be dropped here, or the picker offers OUs
        // that can never match this policy's domain.
        const scoped = (Array.isArray(list) ? list : []).filter((ou) => {
          if (typeof ou === "string") return true;
          const ouAdId = ou?.ad_id ?? ou?.adId;
          if (ouAdId != null) return Number(ouAdId) === Number(adDomainId);
          const adName = ou?.ad_name || ou?.adName;
          return !adName || adName === adDomainFqdn || adName === adDomainLabel;
        });
        setOuOptions(
          scoped
            .map((ou) => {
              if (typeof ou === "string") return { value: ou, label: ou };
              const value = ou?.ou_name || ou?.ouName || ou?.name || ou?.dn;
              if (!value) return null;
              return { value, label: value };
            })
            .filter(Boolean),
        );
        setOuLoading(false);
      },
      onFailure: () => setOuLoading(false),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adDomainId, adDomainFqdn]);

  // ─── Derived / validation ─────────────────────────────────────────────────
  const targetCount = groups.length + users.length + ous.length;
  const badSources = useMemo(
    () => (sourceMode === "specific" ? invalidTokens(sources, isValidSource) : []),
    [sourceMode, sources],
  );
  const badDestinations = useMemo(
    () =>
      destMode === "specific"
        ? invalidTokens(destinations, isValidDestination)
        : [],
    [destMode, destinations],
  );
  const unknownVars = useMemo(() => unknownVariables(mfaPrompt), [mfaPrompt]);

  const problems = useMemo(() => {
    const out = [];
    if (!policyName.trim()) out.push("Give the policy a name.");
    if (!adDomainId) out.push("Pick an AD domain.");
    if (targetCount === 0 && !matchAll)
      out.push(
        "Pick at least one group, user or OU — or tick “Apply to every user in this domain”.",
      );
    if (sourceMode === "specific" && cleanTokens(sources).length === 0)
      out.push("Add at least one source, or switch back to All devices.");
    if (badSources.length > 0)
      out.push(`Source is not an IP or CIDR: ${badSources.join(", ")}`);
    if (destMode === "specific" && cleanTokens(destinations).length === 0)
      out.push("Add at least one destination, or switch back to All destinations.");
    if (badDestinations.length > 0)
      out.push(`Destination isn't a recognised form: ${badDestinations.join(", ")}`);
    return out;
  }, [
    policyName,
    adDomainId,
    targetCount,
    matchAll,
    sourceMode,
    sources,
    badSources,
    destMode,
    destinations,
    badDestinations,
  ]);

  const buildPolicyJson = useCallback(() => {
    return {
      policyType: "AD",
      policyKind: "static",
      authTypes: ["ad"],
      policyFlow: staticAction,
      mfaPromptTemplate: mfaPrompt,
      ad: {
        // The picker submits the domain id; the FQDN travels alongside it.
        // The display label never goes on the wire.
        adId: adDomainId,
        domain: adDomainFqdn,
        groups,
        users,
        ous,
        matchAll,
        sources: sourceMode === "specific" ? cleanTokens(sources) : [],
        destinations: destMode === "specific" ? cleanTokens(destinations) : [],
      },
      // Empty means every protocol.
      protocolControl: { allowed: sanitizeProtocols(protocols) },
    };
  }, [
    staticAction,
    mfaPrompt,
    adDomainId,
    adDomainFqdn,
    groups,
    users,
    ous,
    matchAll,
    sourceMode,
    sources,
    destMode,
    destinations,
    protocols,
  ]);

  // ─── Impact preview ───────────────────────────────────────────────────────
  const runImpactPreview = useCallback(() => {
    if (!adDomainFqdn) {
      setImpact({ state: "error", matchedUsers: null });
      return;
    }
    setImpact({ state: "loading", matchedUsers: null });
    previewPolicyImpact({
      payload: {
        orgId: ORG_ID(),
        tenantId: DOMAIN_ID(),
        domain: adDomainFqdn,
        adGroups: groups,
        adUsers: users,
        adOus: ous,
      },
      onSuccess: (res) => {
        const matched = res?.matchedUsers ?? res?.data?.matchedUsers;
        setImpact({
          state: typeof matched === "number" ? "ready" : "error",
          matchedUsers: typeof matched === "number" ? matched : null,
        });
      },
      onFailure: () => setImpact({ state: "error", matchedUsers: null }),
    });
  }, [adDomainFqdn, groups, users, ous]);

  const goToReview = () => {
    if (problems.length > 0) {
      messageApi.warning(problems[0]);
      return;
    }
    setConflicts(null);
    setStep(2);
    runImpactPreview();
    window.scrollTo({ top: 0 });
  };

  // ─── Dry run ──────────────────────────────────────────────────────────────
  const runDryRun = () => {
    if (!dryRunUser.trim()) {
      messageApi.warning("Enter an AD user to test");
      return;
    }
    setDryRun({ state: "loading", result: null });
    getEffectivePolicy({
      payload: {
        org_id: ORG_ID(),
        tenant_id: DOMAIN_ID(),
        ad_user: dryRunUser.trim(),
        domain: adDomainFqdn,
        protocol: dryRunProtocol,
      },
      onSuccess: (res) => {
        const body = res?.data && res?.data?.action ? res.data : res;
        setDryRun({ state: "ready", result: body || null });
      },
      onFailure: () => setDryRun({ state: "error", result: null }),
    });
  };

  // ─── Publish ──────────────────────────────────────────────────────────────
  const publish = (force = false) => {
    if (problems.length > 0) {
      messageApi.warning(problems[0]);
      setStep(1);
      return;
    }
    setSubmitting(true);
    const policyJson = buildPolicyJson();

    if (isEdit) {
      const id = editingPolicy?.id || editingPolicy?.policyId || routePolicyId;
      if (!id) {
        messageApi.error("Missing policy id — cannot update");
        setSubmitting(false);
        return;
      }
      updateADPolicy({
        payload: {
          id,
          orgId: ORG_ID(),
          tenantId: DOMAIN_ID(),
          policyName,
          policyType: "AD",
          policyJson,
          status: editingPolicy?.status || "Approved",
          version: (editingPolicy?.version || 1) + 1,
          updatedBy: localStorage.getItem("userId") || "",
        },
        onSuccess: (response) => {
          // Updates create a new policy id server-side; adopt it so any
          // further update from this screen targets the active policy, and
          // rewrite the route since the id is part of the URL.
          const newPolicyId = response?.updatedPolicyID?.data;
          if (newPolicyId && newPolicyId !== id) {
            setEditingPolicy((prev) =>
              prev
                ? {
                    ...prev,
                    id: newPolicyId,
                    policyId: newPolicyId,
                    version: (prev.version || 1) + 1,
                  }
                : prev,
            );
            navigate(location.pathname.replace(routePolicyId, newPolicyId), {
              replace: true,
              state: { policy: null },
            });
          }
          if (response?.warning) messageApi.warning(response.warning);
          messageApi.success("Policy updated");
          setSubmitting(false);
          navigate("/pam/policy/v2");
        },
        onFailure: () => {
          messageApi.error("Failed to update policy");
          setSubmitting(false);
        },
      });
      return;
    }

    createADPolicy({
      payload: {
        orgId: ORG_ID(),
        tenantId: DOMAIN_ID(),
        policyName,
        policyType: "AD",
        policyJson,
        generatedBy: "Manual",
        ...(force ? { force: true } : {}),
      },
      onSuccess: (response) => {
        setSubmitting(false);
        setConflicts(null);
        // A 200 can still carry a warning — it's information, not a failure.
        if (response?.warning) messageApi.warning(response.warning);
        messageApi.success("Policy created");
        navigate("/pam/policy/v2");
      },
      onConflict: (payload) => {
        setSubmitting(false);
        setConflicts(payload);
        window.scrollTo({ top: 0 });
      },
      onFailure: () => {
        messageApi.error("Failed to create policy");
        setSubmitting(false);
      },
    });
  };

  // ─── Domain posture ───────────────────────────────────────────────────────
  const switchDomainToEnforce = () => {
    if (!selectedDomain) return;
    setPostureSaving(true);
    dispatchUpdateDomainEnforcement({
      payload: {
        orgId: ORG_ID(),
        tenantId: DOMAIN_ID(),
        adId: selectedDomain.id,
        enforcementMode: "enforce",
        fallbackAction: domainFallback(selectedDomain),
      },
      onSuccess: () => {
        setPostureSaving(false);
        setDomains((prev) =>
          prev.map((d) =>
            d.id === selectedDomain.id ? { ...d, enforcementMode: "enforce" } : d,
          ),
        );
        messageApi.success(`${adDomainLabel} is now enforcing policy.`);
      },
      onFailure: (e) => {
        setPostureSaving(false);
        messageApi.error(e?.message || "Failed to change enforcement mode");
      },
      onError: (e) => {
        setPostureSaving(false);
        messageApi.error(e?.message || "Failed to change enforcement mode");
      },
    });
  };

  if (bootstrapping) {
    return (
      <div
        style={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: AN_COLORS.muted,
          fontFamily: AN_FONT,
        }}
      >
        Loading policy…
      </div>
    );
  }

  return (
    <div
      style={{
        background: AN_COLORS.bgWarm,
        color: AN_COLORS.ink,
        fontFamily: AN_FONT,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {messageContext}

      {/* Header — matches the Policies list page; appends the current step. */}
      <div
        style={{
          padding: "22px 28px 0",
          borderBottom: `1px solid ${AN_COLORS.line}`,
          background: AN_COLORS.bgWarm,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 6,
            gap: 12,
          }}
        >
          <div
            style={{
              fontFamily: AN_MONO,
              fontSize: 10.5,
              letterSpacing: 1.4,
              textTransform: "uppercase",
              color: AN_COLORS.muted,
            }}
          >
            Admin · Policies · AD · {isEdit ? "Edit policy" : "New policy"}
          </div>
          <Link
            to="/pam/policy/v2"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "5px 12px",
              border: `1px solid ${AN_COLORS.line}`,
              borderRadius: 6,
              background: "var(--an-surface)",
              color: AN_COLORS.ink2,
              fontSize: 12.5,
              fontWeight: 500,
              textDecoration: "none",
              fontFamily: AN_FONT,
              whiteSpace: "nowrap",
            }}
          >
            <ArrowLeft size={13} /> Back
          </Link>
        </div>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, letterSpacing: -0.4 }}>
          {isEdit ? "Edit AD policy" : "New AD policy"}
        </h1>
        <p style={{ margin: "4px 0 0", fontSize: 13, color: AN_COLORS.muted }}>
          Define how Active Directory auth requests are evaluated for this scope.
        </p>

        {/* Mini stepper */}
        <div style={{ display: "flex", gap: 8, padding: "16px 0 14px" }}>
          <Stage
            active={step === 1}
            done={step > 1}
            label="1 · Define policy"
            onClick={() => setStep(1)}
          />
          <Stage
            active={step === 2}
            label="2 · Review & publish"
            onClick={step === 2 ? undefined : goToReview}
          />
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, padding: "24px 32px 32px" }}>
        {/* The stepper carries the review; the form runs in one column. */}
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          {legacyNotes.length > 0 && (
            <Callout tone="warn" title="Loaded with changes">
              <ul style={{ margin: "4px 0 0", paddingLeft: 16 }}>
                {legacyNotes.map((n) => (
                  <li key={n} style={{ marginBottom: 2 }}>
                    {n}
                  </li>
                ))}
              </ul>
            </Callout>
          )}

          {step === 1 ? (
            <DefineStep
              policyName={policyName}
              setPolicyName={setPolicyName}
              domains={domains}
              adDomainId={adDomainId}
              setAdDomainId={setAdDomainId}
              staticAction={staticAction}
              setStaticAction={setStaticAction}
              groups={groups}
              setGroups={setGroups}
              users={users}
              setUsers={setUsers}
              ous={ous}
              setOus={setOus}
              matchAll={matchAll}
              setMatchAll={setMatchAll}
              groupPicker={groupPicker}
              onGroupSearch={setGroupQuery}
              userPicker={userPicker}
              onUserSearch={setUserQuery}
              ouOptions={ouOptions}
              ouLoading={ouLoading}
              adDomainLabel={adDomainLabel}
              protocols={protocols}
              setProtocols={setProtocols}
              sourceMode={sourceMode}
              setSourceMode={setSourceMode}
              sources={sources}
              setSources={setSources}
              badSources={badSources}
              destMode={destMode}
              setDestMode={setDestMode}
              destinations={destinations}
              setDestinations={setDestinations}
              badDestinations={badDestinations}
              mfaPrompt={mfaPrompt}
              setMfaPrompt={setMfaPrompt}
              unknownVars={unknownVars}
              targetCount={targetCount}
            />
          ) : (
            <ReviewStep
              isEdit={isEdit}
              policyName={policyName}
              staticAction={staticAction}
              domainLabel={adDomainLabel}
              domainFqdn={adDomainFqdn}
              posture={posture}
              postureSaving={postureSaving}
              onSwitchToEnforce={switchDomainToEnforce}
              impact={impact}
              onRefreshImpact={runImpactPreview}
              matchAll={matchAll}
              groups={groups}
              users={users}
              ous={ous}
              protocols={protocols}
              sourceMode={sourceMode}
              sources={sources}
              destMode={destMode}
              destinations={destinations}
              mfaPrompt={mfaPrompt}
              unknownVars={unknownVars}
              conflicts={conflicts}
              onPublishAnyway={() => publish(true)}
              submitting={submitting}
              dryRun={dryRun}
              dryRunUser={dryRunUser}
              setDryRunUser={setDryRunUser}
              dryRunProtocol={dryRunProtocol}
              setDryRunProtocol={setDryRunProtocol}
              onDryRun={runDryRun}
              adUserOptions={userPicker.options}
            />
          )}
        </div>
      </div>

      {/* Footer action bar — grounded, not floating */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 32px",
          background: AN_COLORS.bgWarm,
          borderTop: `1px solid ${AN_COLORS.line}`,
        }}
      >
        {step === 1 ? (
          <Link
            to="/pam/policy/v2"
            style={{
              fontSize: 13,
              color: AN_COLORS.muted,
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <ArrowLeft size={14} /> Cancel and return to policies
          </Link>
        ) : (
          <button
            type="button"
            onClick={() => setStep(1)}
            style={{
              border: "none",
              background: "transparent",
              padding: 0,
              cursor: "pointer",
              fontSize: 13,
              color: AN_COLORS.muted,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontFamily: AN_FONT,
            }}
          >
            <ArrowLeft size={14} /> Back to define policy
          </button>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {problems.length > 0 && (
            <span style={{ fontSize: 12.5, color: AN_COLORS.danger }}>
              {problems[0]}
            </span>
          )}
          {step === 1 ? (
            <Button
              type="primary"
              size="large"
              onClick={goToReview}
              disabled={problems.length > 0}
            >
              Review &amp; publish <ArrowRight size={14} />
            </Button>
          ) : (
            <Button
              type="primary"
              size="large"
              onClick={() => publish(false)}
              loading={submitting}
              disabled={problems.length > 0 || !!conflicts}
            >
              {isEdit ? "Update policy" : "Publish policy"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Step 1 — define
// ────────────────────────────────────────────────────────────────────────────

function DefineStep({
  policyName,
  setPolicyName,
  domains,
  adDomainId,
  setAdDomainId,
  staticAction,
  setStaticAction,
  groups,
  setGroups,
  users,
  setUsers,
  ous,
  setOus,
  matchAll,
  setMatchAll,
  groupPicker,
  onGroupSearch,
  userPicker,
  onUserSearch,
  ouOptions,
  ouLoading,
  adDomainLabel,
  protocols,
  setProtocols,
  sourceMode,
  setSourceMode,
  sources,
  setSources,
  badSources,
  destMode,
  setDestMode,
  destinations,
  setDestinations,
  badDestinations,
  mfaPrompt,
  setMfaPrompt,
  unknownVars,
  targetCount,
}) {
  const domainOptions = useMemo(
    () =>
      (domains || []).map((d) => ({
        value: d.id,
        label: domainLabel(d),
        fqdn: domainFqdn(d),
      })),
    [domains],
  );

  return (
    <>
      <FormCard
        num="01"
        title="Identity"
        hint="A short, recognisable name and where this policy lives."
      >
        <FormGrid cols="1fr 1fr">
          <FF label="Policy name" required>
            <Input
              value={policyName}
              onChange={(e) => setPolicyName(e.target.value)}
              placeholder="e.g. Domain Admins MFA"
            />
          </FF>
          <FF label="AD domain" required>
            <Select
              value={adDomainId}
              onChange={setAdDomainId}
              showSearch
              placeholder="Search or pick a domain"
              style={{ width: "100%" }}
              options={domainOptions}
              optionFilterProp="label"
              filterOption={(input, option) => {
                const q = input.toLowerCase();
                return (
                  (option?.label || "").toLowerCase().includes(q) ||
                  (option?.fqdn || "").toLowerCase().includes(q)
                );
              }}
              optionRender={(opt) => (
                <div style={{ lineHeight: 1.3, padding: "2px 0" }}>
                  <div style={{ fontSize: 13, color: AN_COLORS.ink }}>
                    {opt.data.label}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      fontFamily: AN_MONO,
                      color: AN_COLORS.muted,
                    }}
                  >
                    {opt.data.fqdn || "no FQDN reported"}
                  </div>
                </div>
              )}
            />
          </FF>
        </FormGrid>
      </FormCard>

      <FormCard num="02" title="Auth type" hint="Where this policy applies.">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          {AUTH_TYPES.map((t) => (
            <ChoiceCard
              key={t.value}
              on={t.value === "ad"}
              disabled={t.disabled}
              badge={t.disabled ? "Coming soon" : null}
              label={t.label}
              sub={t.sub}
              Icon={t.icon}
            />
          ))}
        </div>
      </FormCard>

      <FormCard
        num="03"
        title="Policy type"
        hint="Static is the baseline; risk-based reacts to identity risk score."
      >
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          {POLICY_KINDS.map((k) => (
            <ChoiceCard
              key={k.value}
              on={k.value === "static"}
              disabled={k.disabled}
              badge={k.disabled ? "Coming soon" : null}
              label={k.label}
              sub={k.sub}
              Icon={k.icon}
            />
          ))}
        </div>
        <div
          style={{ marginTop: 10, fontSize: 12, color: AN_COLORS.muted, lineHeight: 1.5 }}
        >
          Every matching request gets the{" "}
          <b style={{ color: AN_COLORS.ink2, fontWeight: 500 }}>same action</b>,
          regardless of context. Use this for guardrails like "Domain Admins always
          MFA".
        </div>
      </FormCard>

      <FormCard
        num="04"
        title="Action"
        hint="Applied to every request that matches this scope."
      >
        <div
          style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}
        >
          {ACTIONS.map((opt) => (
            <ActionCard
              key={opt.value}
              active={staticAction === opt.value}
              label={opt.label}
              hint={opt.hint}
              tone={opt.tone}
              onClick={() => setStaticAction(opt.value)}
            />
          ))}
        </div>
      </FormCard>

      <FormCard
        num="05"
        title="Scope"
        hint="Choose any combination of groups, users or OUs."
      >
        <DirectoryPicker
          label="AD groups"
          picker={groupPicker}
          value={groups}
          onChange={setGroups}
          onSearch={onGroupSearch}
          disabled={!adDomainId}
          disabledPlaceholder="Pick a domain first"
          placeholder="Search AD groups"
          noun="groups"
        />
        <DirectoryPicker
          label="AD users"
          picker={userPicker}
          value={users}
          onChange={setUsers}
          onSearch={onUserSearch}
          disabled={!adDomainId}
          disabledPlaceholder="Pick a domain first"
          placeholder="Search AD users"
          noun="users"
        />
        <FF label="Organizational units" hint="optional">
          <Select
            mode="multiple"
            showSearch
            value={ous}
            onChange={setOus}
            placeholder={
              !adDomainId
                ? "Pick a domain first"
                : ouLoading
                ? "Loading OUs…"
                : ouOptions.length === 0
                ? "No OUs available"
                : "Search and pick OUs"
            }
            loading={ouLoading}
            disabled={!adDomainId}
            options={ouOptions}
            style={{ width: "100%" }}
            optionFilterProp="label"
            filterOption={(input, option) =>
              (option?.label || "").toLowerCase().includes(input.toLowerCase())
            }
            maxTagCount="responsive"
            notFoundContent={ouLoading ? "Loading…" : "No matching OUs"}
          />
        </FF>

        {/* Domain-wide is an explicit choice — an empty selection above never
            means "everyone". */}
        <div
          style={{
            marginTop: 14,
            padding: "12px 14px",
            border: `1px solid ${matchAll ? "#F6D9A8" : AN_COLORS.line}`,
            background: matchAll ? "#FEF9F0" : AN_COLORS.bgWarm,
            borderRadius: 8,
          }}
        >
          <Checkbox
            checked={matchAll}
            onChange={(e) => setMatchAll(e.target.checked)}
          >
            <span style={{ fontSize: 13, color: AN_COLORS.ink }}>
              Apply to every user in this domain
            </span>
          </Checkbox>
          <div
            style={{
              marginTop: 6,
              marginLeft: 24,
              fontSize: 11.5,
              color: AN_COLORS.muted,
              lineHeight: 1.5,
            }}
          >
            {matchAll ? (
              <>
                This policy will match{" "}
                <b style={{ color: "#B05A0A", fontWeight: 600 }}>
                  every user in {adDomainLabel || "the selected domain"}
                </b>
                {targetCount > 0
                  ? ", including the targets picked above."
                  : "."}
              </>
            ) : (
              <>
                Leave this off and the policy only matches the groups, users and OUs
                picked above. An empty selection matches nobody.
              </>
            )}
          </div>
        </div>
      </FormCard>

      {/* AD-04/05/06 — the predicates the engine evaluates alongside identity.
          Numbered 06 because it sits between Scope (05) and MFA prompt. */}
      <FormCard
        num="06"
        title="Context"
        hint="Narrow the policy to specific protocols, sources or destinations."
      >
        <Field label="Protocol" hint="leave empty for all">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginTop: 2 }}>
            {PROTOCOLS.map((p) => (
              <Checkbox
                key={p.value}
                checked={protocols.includes(p.value)}
                onChange={(e) =>
                  setProtocols(
                    e.target.checked
                      ? [...protocols, p.value]
                      : protocols.filter((x) => x !== p.value),
                  )
                }
              >
                <span style={{ fontSize: 13 }}>{p.label}</span>
              </Checkbox>
            ))}
          </div>
          <Hint>
            {protocols.length === 0
              ? "No protocol selected — the policy applies to all four protocols."
              : `Applies to ${protocols.length} of 4 protocols.`}{" "}
            NTLM and SMB are interchangeable in the engine: selecting either
            matches both.
          </Hint>
        </Field>

        <div style={{ height: 14 }} />

        <Field label="Source">
          <Radio.Group
            value={sourceMode}
            onChange={(e) => setSourceMode(e.target.value)}
            style={{ display: "flex", gap: 18, marginBottom: sourceMode === "specific" ? 10 : 0 }}
          >
            <Radio value="all">
              <span style={{ fontSize: 13 }}>All devices</span>
            </Radio>
            <Radio value="specific">
              <span style={{ fontSize: 13 }}>Specific sources</span>
            </Radio>
          </Radio.Group>
          {sourceMode === "specific" && (
            <TokenInput
              value={sources}
              onChange={setSources}
              placeholder="10.0.4.21, 10.0.4.0/24 — press Enter after each"
              invalid={badSources}
              hint={SOURCE_HINT}
            />
          )}
        </Field>

        <div style={{ height: 14 }} />

        <Field label="Destination">
          <Radio.Group
            value={destMode}
            onChange={(e) => setDestMode(e.target.value)}
            style={{ display: "flex", gap: 18, marginBottom: destMode === "specific" ? 10 : 0 }}
          >
            <Radio value="all">
              <span style={{ fontSize: 13 }}>All destinations</span>
            </Radio>
            <Radio value="specific">
              <span style={{ fontSize: 13 }}>Specific</span>
            </Radio>
          </Radio.Group>
          {destMode === "specific" && (
            <TokenInput
              value={destinations}
              onChange={setDestinations}
              placeholder="cifs/fs01.corp.lab — press Enter after each"
              invalid={badDestinations}
              hint={DESTINATION_HINT}
            />
          )}
        </Field>
      </FormCard>

      <FormCard
        num="07"
        title="MFA prompt"
        hint="Shown to the user during step-up. Use variables for context."
      >
        <MfaPromptEditor
          value={mfaPrompt}
          onChange={setMfaPrompt}
          unknownVars={unknownVars}
          domainLabel={adDomainLabel}
        />
      </FormCard>
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Step 2 — review & publish
// ────────────────────────────────────────────────────────────────────────────

function ReviewStep({
  isEdit,
  policyName,
  staticAction,
  domainLabel: label,
  domainFqdn: fqdn,
  posture,
  postureSaving,
  onSwitchToEnforce,
  impact,
  onRefreshImpact,
  matchAll,
  groups,
  users,
  ous,
  protocols,
  sourceMode,
  sources,
  destMode,
  destinations,
  mfaPrompt,
  unknownVars,
  conflicts,
  onPublishAnyway,
  submitting,
  dryRun,
  dryRunUser,
  setDryRunUser,
  dryRunProtocol,
  setDryRunProtocol,
  onDryRun,
  adUserOptions,
}) {
  const actionName = (ACTIONS.find((a) => a.value === staticAction) || ACTIONS[0])
    .label;

  return (
    <>
      {posture === "monitor" && (
        <Callout tone="warn" title={`${label || "This domain"} is in Monitor mode`}>
          Policy decisions are logged, not applied — publishing this policy will not
          prompt or block anyone until the domain is switched to Enforce.
          <div style={{ marginTop: 10 }}>
            <Button size="small" loading={postureSaving} onClick={onSwitchToEnforce}>
              Switch {label || "domain"} to Enforce
            </Button>
          </div>
        </Callout>
      )}

      {conflicts && (
        <ConflictPanel
          conflicts={conflicts}
          onPublishAnyway={onPublishAnyway}
          submitting={submitting}
        />
      )}

      <FormCard
        num="I"
        title="Impact"
        hint="How many AD users this scope resolves to right now."
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 18,
            padding: "16px 18px",
            borderRadius: 8,
            border: `1px solid ${AN_COLORS.line}`,
            background: "linear-gradient(180deg, #FAFBFF 0%, var(--an-tint) 100%)",
          }}
        >
          <div style={{ minWidth: 96 }}>
            {impact.state === "loading" ? (
              <Spin size="small" />
            ) : (
              <div
                style={{
                  fontSize: 30,
                  fontWeight: 600,
                  letterSpacing: -0.6,
                  lineHeight: 1.1,
                  fontVariantNumeric: "tabular-nums",
                  color:
                    impact.state === "ready" ? AN_COLORS.primary : AN_COLORS.muted,
                }}
              >
                {impact.state === "ready"
                  ? impact.matchedUsers.toLocaleString()
                  : "—"}
              </div>
            )}
            <div style={{ fontSize: 11.5, color: AN_COLORS.muted, marginTop: 2 }}>
              matched users
            </div>
          </div>
          <div style={{ flex: 1, fontSize: 12.5, color: AN_COLORS.ink2, lineHeight: 1.55 }}>
            {impact.state === "error" ? (
              <span style={{ color: AN_COLORS.muted }}>
                Impact preview is unavailable right now. It doesn't block publishing —
                the count is informational.
              </span>
            ) : impact.state === "ready" && impact.matchedUsers === 0 ? (
              <span>
                Nothing matches this scope in <Mono>{fqdn || "this domain"}</Mono> yet.
                The policy will publish, but it won't fire until a user matches.
              </span>
            ) : (
              <span>
                Users in <Mono>{fqdn || "this domain"}</Mono> that this scope resolves
                to. They will get <b style={{ fontWeight: 600 }}>{actionName}</b> on a
                matching request.
                {matchAll && (
                  <>
                    {" "}
                    Domain-wide match is on, so the count covers every user in the
                    domain.
                  </>
                )}
              </span>
            )}
          </div>
          <Button size="small" onClick={onRefreshImpact} loading={impact.state === "loading"}>
            Refresh
          </Button>
        </div>
      </FormCard>

      <FormCard num="II" title="What will be published" hint="The policy as the engine will read it.">
        <ReviewRow label="Name">{policyName || "—"}</ReviewRow>
        <ReviewRow label="Domain">
          <span>
            {label} <Mono>{fqdn || "no FQDN"}</Mono>
          </span>
        </ReviewRow>
        <ReviewRow label="Action">
          <ActionPill action={staticAction} />
        </ReviewRow>
        <ReviewRow label="Scope">
          {matchAll ? (
            <span>Every user in {label}</span>
          ) : (
            <span>
              {groups.length} group{groups.length === 1 ? "" : "s"} · {users.length}{" "}
              user{users.length === 1 ? "" : "s"} · {ous.length} OU
              {ous.length === 1 ? "" : "s"}
            </span>
          )}
        </ReviewRow>
        <ReviewRow label="Protocols">
          {protocols.length === 0 ? (
            <span style={{ color: AN_COLORS.muted }}>All protocols</span>
          ) : (
            <Mono>{protocols.join(", ")}</Mono>
          )}
        </ReviewRow>
        <ReviewRow label="Sources">
          {sourceMode === "all" || cleanTokens(sources).length === 0 ? (
            <span style={{ color: AN_COLORS.muted }}>All devices</span>
          ) : (
            <Mono>{cleanTokens(sources).join(", ")}</Mono>
          )}
        </ReviewRow>
        <ReviewRow label="Destinations">
          {destMode === "all" || cleanTokens(destinations).length === 0 ? (
            <span style={{ color: AN_COLORS.muted }}>All destinations</span>
          ) : (
            <Mono>{cleanTokens(destinations).join(", ")}</Mono>
          )}
        </ReviewRow>
        <ReviewRow label="MFA prompt">
          <span style={{ fontSize: 12 }}>{renderMfaPrompt(mfaPrompt)}</span>
        </ReviewRow>
        {unknownVars.length > 0 && (
          <Callout tone="warn" title="Unrecognised variables in the MFA prompt">
            <Mono>{unknownVars.join(", ")}</Mono> will be sent to the phone literally.
          </Callout>
        )}
      </FormCard>

      <FormCard
        num="III"
        title="Dry run"
        hint="Ask the evaluator what a specific user would get today — before this policy exists."
      >
        <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 220px" }}>
            <FF label="AD user" hint="any user in the domain">
              {/* Free text on purpose — the dry run is most useful for a user
                  who isn't in this policy's scope. */}
              <AutoComplete
                value={dryRunUser}
                onChange={(v) => setDryRunUser(v || "")}
                placeholder="jdoe"
                style={{ width: "100%" }}
                options={adUserOptions}
                filterOption={(input, option) =>
                  (option?.value || "").toLowerCase().includes(input.toLowerCase())
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") onDryRun();
                }}
              />
            </FF>
          </div>
          <div style={{ width: 160 }}>
            <FF label="Protocol">
              <Select
                value={dryRunProtocol}
                onChange={setDryRunProtocol}
                style={{ width: "100%" }}
                options={PROTOCOLS}
              />
            </FF>
          </div>
          <Button
            icon={<PlayCircle size={13} />}
            onClick={onDryRun}
            loading={dryRun.state === "loading"}
          >
            Run
          </Button>
        </div>

        {dryRun.state === "error" && (
          <Hint tone="danger">
            The evaluator didn't answer. Nothing was changed.
          </Hint>
        )}
        {dryRun.state === "ready" && dryRun.result && (
          <div
            style={{
              marginTop: 12,
              border: `1px solid ${AN_COLORS.line}`,
              borderRadius: 8,
              padding: 14,
              background: AN_COLORS.bgWarm,
            }}
          >
            <ReviewRow label="Action today">
              <ActionPill action={dryRun.result.action} />
            </ReviewRow>
            <ReviewRow label="MFA required">
              {dryRun.result.mfa_required ? "Yes" : "No"}
            </ReviewRow>
            <ReviewRow label="Match reason">
              <Mono>{dryRun.result.match_reason || "—"}</Mono>
            </ReviewRow>
            <div style={{ fontSize: 11.5, color: AN_COLORS.muted, marginTop: 8 }}>
              This is the currently published policy set — it does not include the
              policy you are about to {isEdit ? "update" : "create"}.
            </div>
          </div>
        )}
      </FormCard>
    </>
  );
}

function ConflictPanel({ conflicts, onPublishAnyway, submitting }) {
  const list = conflicts.conflicts || [];
  return (
    <div
      style={{
        border: "1px solid var(--an-dangerBd)",
        background: "#FDF3F0",
        borderRadius: 10,
        padding: 18,
        marginBottom: 14,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <AlertTriangle size={16} color="var(--an-dangerFg)" style={{ marginTop: 2, flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14.5, fontWeight: 600, color: "var(--an-dangerFg)" }}>
            Not published — {list.length || "some"} conflicting{" "}
            {list.length === 1 ? "policy" : "policies"}
          </div>
          <div style={{ fontSize: 12.5, color: AN_COLORS.ink2, marginTop: 4, lineHeight: 1.5 }}>
            {conflicts.warning ||
              "Another policy already covers part of this scope. Resolve the overlap, or publish anyway and let priority decide."}
          </div>

          {list.length > 0 && (
            <div
              style={{
                marginTop: 12,
                border: `1px solid ${AN_COLORS.line}`,
                borderRadius: 8,
                overflow: "hidden",
                background: "var(--an-surface)",
              }}
            >
              {list.map((c, i) => (
                <div
                  key={c.policyId || c.id || i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    padding: "10px 12px",
                    borderTop: i === 0 ? "none" : `1px solid ${AN_COLORS.line2}`,
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: AN_COLORS.ink }}>
                      {c.policyName || c.name || c.policyId || c.id || "Existing policy"}
                    </div>
                    <div
                      style={{
                        fontSize: 11.5,
                        color: AN_COLORS.muted,
                        marginTop: 2,
                        lineHeight: 1.45,
                      }}
                    >
                      {c.reason || c.message || c.conflictType || "Overlapping scope"}
                    </div>
                  </div>
                  {(c.policyId || c.id) && (
                    <Link
                      to={`/pam/policy/v2/edit/${c.policyId || c.id}`}
                      style={{
                        fontSize: 12.5,
                        color: AN_COLORS.primary,
                        whiteSpace: "nowrap",
                      }}
                    >
                      Open
                    </Link>
                  )}
                </div>
              ))}
            </div>
          )}

          <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 10 }}>
            <Button danger loading={submitting} onClick={onPublishAnyway}>
              Publish anyway
            </Button>
            <span style={{ fontSize: 11.5, color: AN_COLORS.muted }}>
              Re-sends the same policy with <Mono>force: true</Mono>. Both policies
              stay; evaluation order decides.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// MFA prompt editor
// ────────────────────────────────────────────────────────────────────────────

function MfaPromptEditor({ value, onChange, unknownVars, domainLabel: label }) {
  const areaRef = useRef(null);

  const insert = (token) => {
    const el = areaRef.current?.resizableTextArea?.textArea;
    if (!el || typeof el.selectionStart !== "number") {
      onChange(`${value}${value.endsWith(" ") || !value ? "" : " "}${token}`);
      return;
    }
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const next = `${value.slice(0, start)}${token}${value.slice(end)}`;
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      const caret = start + token.length;
      el.setSelectionRange(caret, caret);
    });
  };

  return (
    <>
      <Input.TextArea
        ref={areaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        style={{ fontFamily: AN_MONO, fontSize: 12.5 }}
      />

      <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
        {MFA_VARIABLES.map((v) => (
          <Tooltip key={v.token} title={`${v.desc} — e.g. ${v.sample}`}>
            <button
              type="button"
              onClick={() => insert(v.token)}
              style={{
                padding: "3px 8px",
                borderRadius: 4,
                background: "var(--an-surface)",
                border: `1px solid ${AN_COLORS.line}`,
                color: AN_COLORS.ink2,
                fontSize: 11,
                fontFamily: AN_MONO,
                cursor: "pointer",
              }}
            >
              {v.token}
            </button>
          </Tooltip>
        ))}
      </div>

      {unknownVars.length > 0 && (
        <Hint tone="danger">
          {unknownVars.join(", ")} {unknownVars.length === 1 ? "isn't" : "aren't"} a
          supported variable. Anything unrecognised is pushed to the phone exactly as
          typed.
        </Hint>
      )}

      <div
        style={{
          marginTop: 12,
          padding: 12,
          borderRadius: 8,
          border: `1px solid ${AN_COLORS.line}`,
          background: AN_COLORS.bgWarm,
        }}
      >
        <div
          style={{
            fontSize: 10.5,
            fontFamily: AN_MONO,
            letterSpacing: 1,
            textTransform: "uppercase",
            color: AN_COLORS.muted,
            marginBottom: 6,
          }}
        >
          Preview
        </div>
        <div style={{ fontSize: 13, color: AN_COLORS.ink, lineHeight: 1.5 }}>
          {renderMfaPrompt(value, label ? { $domain: label } : {}) || (
            <span style={{ color: AN_COLORS.muted }}>Nothing to show</span>
          )}
        </div>
      </div>
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Local primitives
// ────────────────────────────────────────────────────────────────────────────

// Multi-select over a server-searched, server-paginated directory list.
// Two things it must never do: filter locally (the server already did, on the
// full set), and present a failed lookup as an empty directory.
function DirectoryPicker({
  label,
  picker,
  value,
  onChange,
  onSearch,
  disabled,
  disabledPlaceholder,
  placeholder,
  noun,
}) {
  const { loading, options, total, error } = picker;
  const shown = options.length;
  const truncated = total > shown;

  return (
    <FF
      label={label}
      hint={
        disabled || error || loading
          ? undefined
          : truncated
          ? `showing ${shown} of ${total} — type to narrow`
          : `${total} ${total === 1 ? noun.replace(/s$/, "") : noun}`
      }
    >
      <Select
        mode="multiple"
        showSearch
        value={value}
        onChange={onChange}
        onSearch={onSearch}
        // The filter is applied server-side; filtering again here would hide
        // results the server deliberately returned.
        filterOption={false}
        placeholder={disabled ? disabledPlaceholder : placeholder}
        loading={loading}
        disabled={disabled}
        options={options}
        style={{ width: "100%" }}
        status={error ? "error" : undefined}
        maxTagCount="responsive"
        notFoundContent={
          loading ? "Loading…" : error ? "Lookup failed" : `No matching AD ${noun}`
        }
      />
      {error && <Hint tone="danger">{error}</Hint>}
      {!error && truncated && (
        <Hint>
          Only the first {shown} of {total} {noun} are listed. Type to search the
          rest — the filter runs against the whole directory, not this page.
        </Hint>
      )}
    </FF>
  );
}

function TokenInput({ value, onChange, placeholder, invalid, hint }) {
  const bad = new Set(invalid || []);
  return (
    <>
      <Select
        mode="tags"
        open={false}
        suffixIcon={null}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        style={{ width: "100%" }}
        tokenSeparators={[",", " ", "\n"]}
        tagRender={({ label, closable, onClose }) => (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              margin: "2px 4px 2px 0",
              padding: "1px 6px",
              borderRadius: 4,
              fontFamily: AN_MONO,
              fontSize: 11.5,
              background: bad.has(label) ? "var(--an-dangerBg)" : AN_COLORS.bg,
              border: `1px solid ${bad.has(label) ? "var(--an-dangerBd)" : AN_COLORS.line}`,
              color: bad.has(label) ? "var(--an-dangerFg)" : AN_COLORS.ink2,
            }}
          >
            {label}
            {closable && (
              <button
                type="button"
                onClick={onClose}
                aria-label={`Remove ${label}`}
                style={{
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  padding: 0,
                  fontSize: 13,
                  lineHeight: 1,
                  color: "inherit",
                }}
              >
                ×
              </button>
            )}
          </span>
        )}
      />
      <Hint tone={invalid && invalid.length > 0 ? "danger" : undefined}>
        {invalid && invalid.length > 0
          ? `Not accepted: ${invalid.join(", ")}. ${hint}`
          : hint}
      </Hint>
    </>
  );
}

function Hint({ children, tone }) {
  return (
    <div
      style={{
        marginTop: 6,
        fontSize: 11.5,
        lineHeight: 1.5,
        color: tone === "danger" ? AN_COLORS.danger : AN_COLORS.muted,
      }}
    >
      {children}
    </div>
  );
}

function Mono({ children }) {
  return (
    <span style={{ fontFamily: AN_MONO, fontSize: 11.5, color: AN_COLORS.ink2 }}>
      {children}
    </span>
  );
}

function Callout({ tone, title, children }) {
  const t =
    tone === "warn"
      ? { bg: "#FEF9F0", bd: "#F6D9A8", fg: "#B05A0A" }
      : { bg: AN_COLORS.primaryL, bd: "var(--an-infoBd)", fg: AN_COLORS.primary };
  return (
    <div
      style={{
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
        padding: 14,
        marginBottom: 14,
        background: t.bg,
        border: `1px solid ${t.bd}`,
        borderRadius: 10,
      }}
    >
      <AlertTriangle size={15} color={t.fg} style={{ marginTop: 1, flexShrink: 0 }} />
      <div style={{ flex: 1, fontSize: 12.5, color: AN_COLORS.ink2, lineHeight: 1.5 }}>
        {title && (
          <div style={{ fontWeight: 600, color: t.fg, marginBottom: 2, fontSize: 13 }}>
            {title}
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

function ReviewRow({ label, children }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 16,
        padding: "8px 0",
        borderBottom: `1px dashed ${AN_COLORS.line2}`,
        fontSize: 12.5,
      }}
    >
      <span style={{ color: AN_COLORS.muted }}>{label}</span>
      <span style={{ textAlign: "right", color: AN_COLORS.ink2, minWidth: 0 }}>
        {children}
      </span>
    </div>
  );
}

function Stage({ label, active, done, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 12px",
        background: active ? AN_COLORS.ink : "transparent",
        color: active ? AN_COLORS.surface : AN_COLORS.muted,
        border: `1px solid ${active ? AN_COLORS.ink : AN_COLORS.line}`,
        borderRadius: 99,
        fontSize: 11.5,
        fontWeight: 500,
        fontFamily: AN_FONT,
        letterSpacing: 0.1,
        cursor: onClick ? "pointer" : "default",
      }}
    >
      {done && <Check size={11} />}
      {label}
    </button>
  );
}

function FormCard({ num, title, hint, children }) {
  return (
    <div
      style={{
        background: "var(--an-surface)",
        border: `1px solid ${AN_COLORS.line}`,
        borderRadius: 10,
        padding: 22,
        marginBottom: 14,
        boxShadow: "0 1px 2px rgba(16,22,60,.03)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 14,
          marginBottom: 16,
        }}
      >
        <span
          style={{
            fontFamily: AN_MONO,
            fontSize: 10.5,
            color: AN_COLORS.muted,
            letterSpacing: 1.2,
            marginTop: 3,
            padding: "2px 7px",
            background: AN_COLORS.bgWarm,
            border: `1px solid ${AN_COLORS.line2}`,
            borderRadius: 4,
          }}
        >
          {num}
        </span>
        <div style={{ flex: 1 }}>
          <h3
            style={{
              margin: 0,
              fontSize: 15,
              fontWeight: 600,
              letterSpacing: -0.2,
              color: AN_COLORS.ink,
            }}
          >
            {title}
          </h3>
          {hint && (
            <p
              style={{
                margin: "3px 0 0",
                fontSize: 12.5,
                color: AN_COLORS.muted,
                lineHeight: 1.45,
              }}
            >
              {hint}
            </p>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}

function FormGrid({ cols, children }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: cols, gap: 14 }}>
      {children}
    </div>
  );
}

function FF({ label, hint, required, children }) {
  return (
    <label style={{ display: "block", marginBottom: 0 }}>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          marginBottom: 6,
        }}
      >
        <span style={{ fontSize: 12.5, fontWeight: 500, color: AN_COLORS.ink2 }}>
          {label}
          {required && (
            <span style={{ color: AN_COLORS.muted, fontWeight: 400, marginLeft: 3 }}>
              *
            </span>
          )}
        </span>
        {hint && (
          <span style={{ fontSize: 11.5, color: AN_COLORS.muted }}>{hint}</span>
        )}
      </div>
      {children}
    </label>
  );
}

// Same header as FF, but a plain div — for fields whose control is a radio
// group or a set of checkboxes, where a wrapping <label> would hijack clicks.
function Field({ label, hint, children }) {
  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          marginBottom: 6,
        }}
      >
        <span style={{ fontSize: 12.5, fontWeight: 500, color: AN_COLORS.ink2 }}>
          {label}
        </span>
        {hint && (
          <span style={{ fontSize: 11.5, color: AN_COLORS.muted }}>{hint}</span>
        )}
      </div>
      {children}
    </div>
  );
}

// Selection card used for both auth type and policy type. `disabled` renders
// the card as unavailable and ignores clicks — the card stays visible so the
// option is discoverable, with its badge saying why it can't be picked.
function ChoiceCard({ on, disabled, badge, label, sub, Icon, onClick }) {
  const active = on && !disabled;
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={disabled ? `${label} — ${badge}` : undefined}
      style={{
        flex: "1 1 0",
        minWidth: 180,
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        padding: "12px 14px",
        background: active ? AN_COLORS.primaryL : "#FFFFFF",
        border: `1px solid ${active ? AN_COLORS.primary : AN_COLORS.line}`,
        borderRadius: 8,
        cursor: disabled ? "not-allowed" : onClick ? "pointer" : "default",
        textAlign: "left",
        boxShadow: active ? `0 0 0 3px ${anAlpha(AN_COLORS.primaryL, 53)}` : "none",
        transition: "all .12s",
        fontFamily: AN_FONT,
        opacity: disabled ? 0.55 : 1,
      }}
    >
      <span
        style={{
          width: 32,
          height: 32,
          borderRadius: 7,
          flexShrink: 0,
          background: active ? "var(--an-surface)" : AN_COLORS.bg,
          color: active ? AN_COLORS.primary : AN_COLORS.muted,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          border: active
            ? `1px solid ${anAlpha(AN_COLORS.primary, 20)}`
            : `1px solid ${AN_COLORS.line}`,
        }}
      >
        {Icon ? <Icon size={16} /> : null}
      </span>
      <span style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span
            style={{
              fontSize: 13.5,
              fontWeight: 600,
              letterSpacing: -0.1,
              color: active ? AN_COLORS.primary : AN_COLORS.ink,
              fontFamily: AN_FONT,
            }}
          >
            {label}
          </span>
          {badge && (
            <span
              style={{
                padding: "1px 6px",
                borderRadius: 4,
                background: AN_COLORS.bg,
                border: `1px solid ${AN_COLORS.line}`,
                color: AN_COLORS.muted,
                fontSize: 9.5,
                fontFamily: AN_MONO,
                fontWeight: 600,
                letterSpacing: 0.4,
                textTransform: "uppercase",
                whiteSpace: "nowrap",
              }}
            >
              {badge}
            </span>
          )}
        </span>
        <span
          style={{
            fontSize: 11,
            color: active ? AN_COLORS.primary : AN_COLORS.muted,
            opacity: active ? 0.85 : 1,
            fontFamily: AN_MONO,
            letterSpacing: 0.2,
          }}
        >
          {sub}
        </span>
      </span>
      <span
        style={{
          width: 16,
          height: 16,
          borderRadius: "50%",
          border: `1.5px solid ${active ? AN_COLORS.primary : AN_COLORS.line}`,
          background: active ? AN_COLORS.primary : "#FFFFFF",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          marginTop: 2,
          flexShrink: 0,
        }}
      >
        {active && (
          <svg width="8" height="8" viewBox="0 0 9 9" fill="none">
            <path
              d="M2 4.5l2 2 4-4"
              stroke="#fff"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </span>
    </button>
  );
}

function ActionCard({ active, label, hint, tone, onClick }) {
  const t = ACTION_TONES[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 6,
        padding: "12px 14px",
        textAlign: "left",
        background: active ? t.bg : "var(--an-surface)",
        border: `1px solid ${active ? t.fg : AN_COLORS.line}`,
        borderRadius: 8,
        cursor: "pointer",
        boxShadow: active ? `0 0 0 3px ${t.bg}` : "none",
        transition: "all .12s",
        fontFamily: AN_FONT,
      }}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: 13,
          fontWeight: 600,
          color: active ? t.fg : AN_COLORS.ink,
        }}
      >
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: t.fg }} />
        {label}
      </span>
      <span style={{ fontSize: 11.5, color: AN_COLORS.muted, lineHeight: 1.35 }}>
        {hint}
      </span>
    </button>
  );
}
